import _, { get, size } from "lodash";
import { useEffect, useState } from "react";
import { Button, cn } from "@shared/ui";
import { ChevronDown } from "lucide-react";
import { Field, InlineError, inputClassName } from "../components/Field";
import StepBody from "../components/StepBody";
import StepFooter from "../components/StepFooter";
import StepHeading from "../components/StepHeading";
import CarrierMark from "../components/CarrierMark";
import { processAlternativeLink } from "../lib/axle";
import { handleProcessAlternativeLinkError } from "../lib/errorHandlersUtility";
import { createEvent } from "../lib/ignitionEvents";
import { getFieldHint, getFieldLabel } from "../lib/fieldCopy";
import { validateIntegrationUserFields } from "../lib/utility";
import CarrierLoader from "./CarrierLoader";

const PolicyLookup = ({
  step,
  nextStep,
  setAccountInfo,
  setPolicyInfo,
  loginInformation,
  setLoginInformation,
  linkAttempts,
  setLinkAttempts,
  session,
  setLinkError,
  posthog,
  setShowNav,
  client,
}) => {
  // Compute these values first so they can be used in useState
  const carrier = get(loginInformation, "carrier.id");
  const ignitionToken = session.id;
  const policyTypesFromSession = get(session, "config.scopes.policyTypes", []);
  const carrierConfig = session.carriers.find(
    (carrierConfig) => carrierConfig.id === carrier,
  );
  const carrierIntegrations = get(carrierConfig, "integrations", []);

  const lpIntegration = carrierIntegrations.find(
    (integration) => integration.type === "L123",
  );
  const integrationName = get(lpIntegration, "integration");

  const userInputFieldsConfig = get(lpIntegration, "userInputFields", []);

  // Get available policy types from integration
  const availablePolicyTypesFromIntegration = get(
    lpIntegration,
    "policyTypes",
    [],
  );

  // Filter policyTypesFromSession to only include types that are available to the integration
  const filteredPolicyTypes = policyTypesFromSession
    .filter((type) => availablePolicyTypesFromIntegration.includes(type))
    .filter((type) => type !== "condo"); // Remove condo from dropdown options

  const [userFields, setUserFields] = useState({});
  const [promiseInProgress, setPromiseInProgress] = useState(false);
  const [error, setError] = useState("");
  // Set when a field failed validation, so its message shows under that field.
  const [errorField, setErrorField] = useState(null);

  // Auto-select when there is only one policy type; otherwise require an explicit selection
  useEffect(() => {
    if (filteredPolicyTypes.length === 1) {
      setUserFields((prev) => ({
        ...prev,
        policyType: filteredPolicyTypes[0],
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    posthog.capture("$pageview", { step, carrier });
  }, [posthog, step, carrier]);

  // Null until the user makes a selection (or auto-selected when only one type exists)
  const selectedPolicyType = userFields.policyType || null;

  // Fields with no policyTypes restriction are always shown (e.g. policy number)
  const baseFields = _.filter(
    userInputFieldsConfig,
    (f) => f.type !== "dropdown" && _.isEmpty(f.policyTypes),
  );

  // Fields scoped to the selected policy type (e.g. VIN for auto, postal code for home)
  const conditionalFields = _.filter(
    userInputFieldsConfig,
    (f) =>
      f.type !== "dropdown" && _.includes(f.policyTypes, selectedPolicyType),
  );

  // Validate only the fields that are currently visible
  const activeFieldsConfig = [...baseFields, ...conditionalFields];

  // Clear policy-type-specific field values when the selection changes
  useEffect(() => {
    const staleFields = _.chain(userInputFieldsConfig)
      .filter(
        (f) =>
          !_.isEmpty(f.policyTypes) &&
          !_.includes(f.policyTypes, selectedPolicyType),
      )
      .map("value")
      .value();
    setUserFields((prev) => _.omit(prev, staleFields));
  }, [selectedPolicyType]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadingSteps = [
    "Establishing a secure connection",
    "Contacting your carrier",
    "Retrieving policy information",
  ];

  if (filteredPolicyTypes.length === 0) {
    console.error(
      "No available policy types found for integration after filtering",
      integrationName,
      availablePolicyTypesFromIntegration,
      policyTypesFromSession,
    );
    nextStep("carrier-error");
    return;
  }

  if (size(userInputFieldsConfig) === 0) {
    console.error("No user input fields config found for carrier", carrier);
    nextStep("carrier-error");
    return;
  }

  const onSubmit = async (e) => {
    e.preventDefault();

    // Validate only the fields that are currently visible
    const [isValid, errorMessage, invalidField] = validateIntegrationUserFields(
      userFields,
      activeFieldsConfig,
    );
    setErrorField(isValid ? null : invalidField);
    if (!isValid) {
      setError(errorMessage);
      return;
    }

    // Track policy lookup submit
    posthog.capture("policy-lookup-submit", {
      step,
      carrier,
      linkAttempts: get(linkAttempts, carrier, 0) + 1,
    });

    // Remove the nav bar while loading
    setShowNav(false);

    // Mark this session as having attempted a link, regardless of successful
    // or not — used to compute link-attempt-based conversion metrics
    await createEvent({
      ignitionToken,
      status: "attempted",
      result: "link",
      resultDetail: "lienholder-portal",
      session,
      client,
      carrier,
      integration: integrationName,
    });

    setPromiseInProgress(true);

    // Call processAlternativeLink handler
    const [
      processAlternativeLinkResponse,
      processAlternativeLinkResponseError,
    ] = await processAlternativeLink(
      ignitionToken,
      carrier,
      integrationName,
      userFields,
    );

    // Restore the nav bar
    setShowNav(true);

    if (processAlternativeLinkResponseError) {
      handleProcessAlternativeLinkError({
        error: processAlternativeLinkResponseError,
        linkAttempts,
        setLinkAttempts,
        setError,
        nextStep,
        setPromiseInProgress,
        setLinkError,
        carrier,
      });
      return;
    }

    // We have a successful response from Alternative Link
    const accountId = get(processAlternativeLinkResponse, "accountId", "");
    const policyId = get(processAlternativeLinkResponse, "policyId", "");
    const processDetail = get(
      processAlternativeLinkResponse,
      "processDetail",
      "",
    );

    // Setting account and policy info
    setAccountInfo({ id: accountId });
    setPolicyInfo({ id: policyId });

    setLoginInformation({
      ...loginInformation,
      result: "link",
      resultDetail: processDetail,
    });

    nextStep("success");
    return;
  };

  // Enhanced manual: "Search using document" on select-verification-method.
  const hasEnhancedManualOption =
    get(session, "config.manual.enabled") && get(session, "config.manual.enhanced");
  // Non-L123 means user-portal login, etc. - any option other than policy-lookup.
  const hasNonPolicyLookupOption = carrierIntegrations.some((i) => i.type !== "L123");
  const showUseDifferentOption = hasEnhancedManualOption || hasNonPolicyLookupOption;

  return (
    <>
      {promiseInProgress ? (
        <CarrierLoader
          loginInformation={loginInformation}
          loadingHeader="Searching for your policy"
          loadingSteps={loadingSteps}
          showFooter={false}
        />
      ) : (
        <form className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit}>
          <StepBody>
            <CarrierMark loginInformation={loginInformation} />
            <StepHeading
              title="Enter your policy information"
              description={
                <>
                  We’ll use your{" "}
                  <span className="font-medium text-ink">
                    {loginInformation.carrier.name}
                  </span>{" "}
                  policy details to securely find your policy.
                </>
              }
            />
            <div className="flex flex-col gap-4">
              {error && !errorField && (
                <div
                  className="rounded border border-danger-border p-3"
                  role="status"
                >
                  <InlineError>{error}</InlineError>
                </div>
              )}

              {/* Policy type dropdown */}
              <Field id="policyType" label="Policy type">
                <div className="relative">
                  <select
                    id="policyType"
                    value={userFields.policyType || ""}
                    onChange={(e) =>
                      setUserFields({
                        ...userFields,
                        policyType: e.target.value,
                      })
                    }
                    className={cn(
                      inputClassName,
                      "appearance-none pr-10",
                      !userFields.policyType && "text-ink-secondary"
                    )}
                  >
                    <option value="" disabled>
                      Select a policy type
                    </option>
                    {filteredPolicyTypes.map((type) => (
                      <option key={type} value={type} className="text-ink">
                        {_.capitalize(type)}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    aria-hidden
                    strokeWidth={1.75}
                    className="pointer-events-none absolute right-3 top-1/2 size-[18px] -translate-y-1/2 text-ink"
                  />
                </div>
              </Field>

              {/* Remaining fields revealed only after a policy type is selected */}
              {selectedPolicyType &&
                [...baseFields, ...conditionalFields].map((item) => (
                  <Field
                    key={item.value}
                    id={item.value}
                    label={getFieldLabel(item)}
                    optional={item.required === false}
                    error={errorField === item.value ? error : undefined}
                  >
                    <input
                      id={item.value}
                      aria-invalid={errorField === item.value}
                      aria-describedby={
                        errorField === item.value ? `${item.value}-error` : undefined
                      }
                      placeholder={getFieldHint(item)}
                      value={userFields[item.value] || ""}
                      className={inputClassName}
                      type={item.type}
                      onChange={(e) =>
                        setUserFields({
                          ...userFields,
                          [item.value]: e.target.value,
                        })
                      }
                    />
                  </Field>
                ))}
            </div>
          </StepBody>

          <StepFooter>
            <Button type={"submit"} disabled={!selectedPolicyType}>
              Search
            </Button>
            {/* Hide when only policy number lookup applies, otherwise user loops back to the same screen. */}
            {showUseDifferentOption && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => nextStep("select-verification-method")}
              >
                Use a different option
              </Button>
            )}
          </StepFooter>
        </form>
      )}
    </>
  );
};

export default PolicyLookup;
